<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $ } from '@bagelink/vue'
const defaults = {
	articles: [
		{
			title: 'Sample Article',
			subtitle: 'A short introduction to the article.',
			image: '/og.jpg',
			link: '/article/sample',
			authors: [{ name: 'Author Name', title: 'Author Title', image: '/webclip.png' }],
		},
	],
	topics: [],
	showTopics: 'false',
	centered: 'false',
	showAuthors: 'true',
}
const schema = defineSchema({
	articles: $.array(
		'Articles',
		defineSchema({
			title: $.text('Title'),
			subtitle: $.text('Subtitle'),
			image: $.upload('Image'),
			link: $.text('Link'),
			authors: $.array(
				'Authors',
				defineSchema({
					name: $.text('Name'),
					title: $.text('Title'),
					image: $.upload('Image'),
				}),
				{ collapsible: true },
			),
		}),
		{ collapsible: true },
	),
	topics: $.array(
		'Topics',
		defineSchema({
			name: $.text('Name'),
			link: $.text('Link'),
		}),
		{ collapsible: true },
	),
	showTopics: $.select('Show Topics', [
		{ label: 'Yes', value: 'true' },
		{ label: 'No', value: 'false' },
	]),
	centered: $.select('Centered', [
		{ label: 'Yes', value: 'true' },
		{ label: 'No', value: 'false' },
	]),
	showAuthors: $.select('Show Authors', [
		{ label: 'Yes', value: 'true' },
		{ label: 'No', value: 'false' },
	]),
})
export const BlockArticleList = defineBlock({
	thumbnail: '/block-thumbs/BlockArticleList.svg',
	category: 'Listings',
	order: 110,
	name: 'BlockArticleList',
	label: 'Article List',
	icon: 'list',
	schema,
	defaults,
})
</script>
<script setup lang="ts">

import { Image } from '@bagelink/vue'
import AuthorSmallLink from '@/components/blocks/AuthorSmallLink.vue'
import { articleTitleText, sanitizeArticleHtml, sanitizeArticleTitleHtml } from '@/utils/articleHtml'




interface Author {
	name: string
	title: string
	image: string
	link?: string
}

interface Article {
	title: string
	authors: Author[]
	image: string
	link: string
	subtitle?: string
}

interface Topic {
	name: string
	link: string
}

defineProps<{
	articles?: Article[]
	topics?: Topic[]
	showTopics?: string
	centered?: string
	showAuthors?: string
}>()
</script>

<template>
	<div
		v-if="articles?.length || (showTopics === 'true' && topics?.length)"
		class="w1000px flex gap-3 space-between py-2 align-items-start m_px-0 m_pt-1 m_w-100i"
	>
		<!-- gap-2 m_pe-1 -->
		<!-- flex gap-2 space-between align-items-start m_pe-1 -->
		<!-- mx-auto display-flex column gap-2 -->
		<!--  gap-2 -->

		<div
			v-if="articles?.length"
			class="w720px display-flex column gap-3 w-100i m_gap-2"
			:class="{ 'mx-auto': showTopics !== 'true' || !topics?.length }"
		>
			<article v-for="article in articles" :key="article.link">
				<div
					class="hover flex gap-1 align-items-start decoration-none w-100p"
					:class="{ m_block: centered === 'true' }"
				>
					<RouterLink
						:to="article.link"
						class="flex-shrink-0"
						:class="{ 'm_w-100p': centered === 'true' }"
					>
						<Image
							:src="article.image || '/og.jpg'"
							:alt="articleTitleText(article.title)"
							ratio="4-3"
							class="aspect-ratio-4-3 h-110px cover flex-shrink-0 bg-gray"
							:class="{
								' m_h60px': centered !== 'true',
								'm_h-200px m_w-100p m_mb-05': centered === 'true',
							}"
						/>
					</RouterLink>
					<div :class="{ 'txt-center w-100p': centered === 'true' }">
						<RouterLink :to="article.link" class="decoration-none">
							<h4
								class="my-0 regular pb-05"
								:class="{
									'txt-center bold txt21 line-height-15': centered === 'true',
									txt17: centered !== 'true',
								}"
							>
							<span v-html="sanitizeArticleTitleHtml(article.title)" />
						</h4>
						</RouterLink>
						<div
							v-if="article.subtitle"
							class="light pb-025 ellipsis-2"
							v-html="sanitizeArticleHtml(article.subtitle)"
						></div>
						<div v-if="showAuthors !== 'false'">
							<AuthorSmallLink
								v-for="author in article.authors"
								:key="author.name"
								:author="author"
								:show-image="false"
							/>
						</div>
					</div>
				</div>
			</article>
		</div>

		<!-- Topics Sidebar -->
		<aside v-if="showTopics === 'true' && topics?.length" class="m_none">
			<h3 class="txt17 semibold my-0 pb-2">Browse Topics:</h3>
			<router-link
				v-for="topic in topics"
				:key="topic.link"
				:to="topic.link"
				class="txt17 redhover block decoration-none light mb-1"
			>
				{{ topic.name }}
			</router-link>
		</aside>
	</div>
</template>
